import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Radio,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PlayArrow as RunIcon,
  PictureAsPdf as PdfIcon,
  MenuBook as GuideIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as OkIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
} from "@mui/icons-material";

import ReportProcessDialog from "./ReportProcessDialog";
import ReportDeleteDialog from "./ReportDeleteDialog";
import ReportUploadDialog from "./ReportUploadDialog";
import MoveToProjectDialog from "./projects/MoveToProjectDialog";
import InputValidationReportDialog from "./InputValidationReportDialog";
import InputProblemsDialog from "./components/InputProblemsDialog";
import { blockingFindings, problemsFromError } from "./utils/inputProblems";
import QuickUserGuideDialog from "./QuickUserGuideDialog";
import UserAvatar from "./components/UserAvatar";
import { useAuthStore } from "./store/authStore";
import { useReportsStore } from "./store/reportsStore";
import apiClient from "./utils/apiClient";
import { formatDate } from "./DateUtils";
import { projectPayload, projectValueOf, useProjectTags } from "./projects/ProjectTagField";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const templateLink = "/input_templates/ContinuousMFA-v3.2-input-template-SOP.pdf";

// Input filenames routinely share a long common prefix (product/experiment
// naming conventions) and differ only near the end — end-ellipsis (the CSS
// default) hides exactly the part that distinguishes them. Truncate from the
// middle instead, keeping the extension intact, so both the identifying
// prefix and the distinguishing suffix stay visible. The full name is always
// still available via the row's tooltip.
function middleTruncate(name, maxLength = 56) {
  if (!name || name.length <= maxLength) return name || "";
  const dotIndex = name.lastIndexOf(".");
  const hasExt = dotIndex > 0 && name.length - dotIndex <= 8;
  const ext = hasExt ? name.slice(dotIndex) : "";
  const base = hasExt ? name.slice(0, dotIndex) : name;
  const keep = maxLength - ext.length - 1; // 1 char for the ellipsis
  if (keep <= 4) return `${name.slice(0, maxLength - 1)}…`;
  const head = Math.ceil(keep * 0.6);
  const tail = keep - head;
  return `${base.slice(0, head)}…${base.slice(base.length - tail)}${ext}`;
}

// Severity → presentation helpers used by the badge AND the per-finding list.
const SEV_PRESENTATION = {
  critical: { color: "#EF4444", dot: "🔴", label: "critical" },
  warning:  { color: "#F59E0B", dot: "🟠", label: "warning"  },
  info:     { color: "#3B82F6", dot: "ℹ️", label: "info"     },
};

function ValidationBadge({ record, onClickDetail }) {
  const status = record.validation_status;
  const findings = record.findings || [];

  if (!status) {
    return <Typography variant="caption" sx={{ color: "#94A3B8" }}>—</Typography>;
  }

  if (status === "ok") {
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <OkIcon sx={{ fontSize: 13, color: "#10B981" }} />
        <Typography variant="caption" sx={{ color: "#10B981", fontWeight: 600 }}>OK</Typography>
      </Box>
    );
  }

  // Group findings by check name. Each entry shows: severity dot, check name, count.
  // This is what the user wants visible at-a-glance — no hover required.
  const byCheck = {};
  for (const f of findings) {
    const key = f.check || "unknown";
    if (!byCheck[key]) {
      byCheck[key] = { severity: f.severity || "warning", count: 0 };
    }
    byCheck[key].count += 1;
  }
  // Order: critical first, then warning, then info; within a tier, by count desc.
  const tierOrder = { critical: 0, warning: 1, info: 2 };
  const checkList = Object.entries(byCheck)
    .map(([name, { severity, count }]) => ({ name, severity, count }))
    .sort((a, b) =>
      (tierOrder[a.severity] ?? 9) - (tierOrder[b.severity] ?? 9) || b.count - a.count
    );

  const isError = status === "error";
  const headerColor = isError ? "#EF4444" : "#F59E0B";
  const HeaderIcon = isError ? ErrorIcon : WarningIcon;
  const headerLabel = isError ? "Error" : "Warning";

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        if (onClickDetail) onClickDetail(record);
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        py: 0.25,
        cursor: onClickDetail ? "pointer" : "default",
        "&:hover": onClickDetail ? { opacity: 0.85 } : undefined,
      }}
    >
      {/* Status line */}
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <HeaderIcon sx={{ fontSize: 13, color: headerColor }} />
        <Typography variant="caption" sx={{ color: headerColor, fontWeight: 600 }}>
          {headerLabel}
        </Typography>
        {findings.length > 0 && (
          <Typography variant="caption" sx={{ color: "#64748B", fontSize: "0.68rem" }}>
            · {findings.length} finding{findings.length === 1 ? "" : "s"}
          </Typography>
        )}
      </Box>

      {/* Per-check breakdown — visible without hovering. Click to open full dialog. */}
      {checkList.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.1, pl: 0.5 }}>
          {checkList.map(({ name, severity, count }) => {
            const sev = SEV_PRESENTATION[severity] || SEV_PRESENTATION.warning;
            return (
              <Box
                key={name}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "0.7rem",
                  color: "#475569",
                }}
                title={`${name} (${sev.label}, ${count})`}
              >
                <span style={{ fontSize: "0.7rem" }}>{sev.dot}</span>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.7rem",
                    color: "#475569",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.68rem",
                    color: "#94A3B8",
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  ×{count}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {onClickDetail && (
        <Typography
          variant="caption"
          sx={{ fontSize: "0.65rem", color: "#94A3B8", mt: 0.25, fontStyle: "italic" }}
        >
          click for full report
        </Typography>
      )}
    </Box>
  );
}

// What a user (non-Admin) sees: ready, or how many problems block processing.
// Their /input-list rows carry only the blocking (critical) findings.
function UserValidationBadge({ record, onViewProblems }) {
  const status = record.validation_status;
  const problems = blockingFindings(record.findings);
  if (!status) {
    return <Typography variant="caption" sx={{ color: "#94A3B8" }}>—</Typography>;
  }
  if (problems.length === 0) {
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <OkIcon sx={{ fontSize: 13, color: "#10B981" }} />
        <Typography variant="caption" sx={{ color: "#10B981", fontWeight: 600 }}>Ready</Typography>
      </Box>
    );
  }
  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onViewProblems(record, problems);
      }}
      sx={{ cursor: "pointer", "&:hover": { opacity: 0.85 } }}
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <ErrorIcon sx={{ fontSize: 13, color: "#EF4444" }} />
        <Typography variant="caption" sx={{ color: "#EF4444", fontWeight: 600 }}>
          Fix needed · {problems.length} problem{problems.length === 1 ? "" : "s"}
        </Typography>
      </Box>
      <Typography variant="caption" component="div" sx={{ fontSize: "0.65rem", color: "#B91C1C", textDecoration: "underline" }}>
        view problems
      </Typography>
    </Box>
  );
}

const fileNameOf = (record) =>
  record ? (Array.isArray(record.files) ? record.files[0] : record.files || record.id) : null;

// ─── Main component ───────────────────────────────────────────────────────────

// `project` (a project ID, or "none" for the Metalytics catch-all) lists every
// user's files in it instead of one user's — staff only, from a project page.
// Uploads and runs started here are the caller's own.
export default function ReportInputTable({ userId = null, project = null }) {
  const user = useAuthStore((state) => state.user);
  const isAdmin = Array.isArray(user?.roles) && user.roles.includes("Admin");
  const triggerReportsRefresh = useReportsStore((s) => s.triggerRefresh);
  const [validationReportRecord, setValidationReportRecord] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteFileId, setDeleteFileId] = useState(null);
  const [isUploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [isInputTableVisible, setIsInputTableVisible] = useState(!!project);
  const ownerId = project ? user?.id : userId;
  const [moving, setMoving] = useState(null);
  const [inputFiles, setInputFiles] = useState([]);
  const [isProcessDialogOpen, setIsProcessDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  // The caller's company's project tags, for the upload and process dialogs.
  const projectTags = useProjectTags();
  const [selectedClones, setSelectedClones] = useState(null);
  const [availableClones, setAvailableClones] = useState([]);
  const [dialogClones, setDialogClones] = useState([]);
  const [uploadMessage, setUploadMessage] = useState(null);
  // {fileName, message, findings} — the problems dialog, opened from a badge,
  // the blocked-run notice, a refused run start or an upload with errors.
  const [problems, setProblems] = useState(null);
  const showProblems = (record, findings) =>
    setProblems({ fileName: fileNameOf(record), message: null, findings });
  const [isUserGuideOpen, setIsUserGuideOpen] = useState(false);

  const fetchData = async () => {
    try {
      const inputsRes = project
        ? await apiClient.get(`/input-list?project=${encodeURIComponent(project)}`)
        : await apiClient.get("/input-list?user_id=" + userId);
      setInputFiles(inputsRes.sort((a, b) => b.upload_date - a.upload_date));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (!userId && !project) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when the list changes, not on fetchData identity
  }, [userId, project]);

  const handleFileSelect = (record) => {
    setSelectedFile(record.id);
    setAvailableClones(record.clones || []);
    setSelectedClones(record.clones || []);
  };

  const handleOpenProcessDialog = () => {
    setDialogClones(selectedClones || []);
    setIsProcessDialogOpen(true);
  };

  const handleProcessInputFile = (
    selectedProduct,
    usePostProcessing,
    selectBioInterpreter,
    clonesToProcess,
    projectTag
  ) => {
    const safeClones = Array.isArray(clonesToProcess) ? clonesToProcess : [];
    // Every offered condition checked = run everything: send no selection, so
    // the run never filters out a condition the list happened to miss (lists
    // stored for older uploads came from the INTRO sheet, not the data).
    const allSelected =
      availableClones.length > 0 && availableClones.every((c) => safeClones.includes(c));
    apiClient
      .post("/run", {
        id: apiClient.generateTimestampId(),
        product: selectedProduct,
        use_postprocessing: usePostProcessing,
        use_biointerpreter: selectBioInterpreter,
        title: `${selectedProduct} Report`,
        user_id: ownerId,
        input_files: [selectedFile],
        clones: allSelected ? [] : safeClones,
        output_dir: "output_dir",
        ...projectPayload(projectTags, projectTag),
      })
      .then((response) => {
        console.log("Processing started:", response);
        fetchData();
        // Job History (ReportTable) polls independently every 10s — nudge
        // it to refetch now instead of leaving the new run invisible until
        // its next poll tick.
        triggerReportsRefresh();
      })
      .catch((error) => {
        console.error("Error processing input file:", error);
        const refused = problemsFromError(error);
        if (refused) {
          setProblems({ fileName: refused.inputFile || selectedFileName, message: refused.message, findings: refused.findings });
          // The file was checked again at launch; refresh so its row shows it.
          fetchData();
        } else {
          setUploadMessage({ type: "error", title: "Couldn't start the analysis", message: error?.message || "The run could not be started." });
        }
      });
    setSelectedClones(safeClones);
  };

  const handleDeleteFile = async () => {
    try {
      await apiClient.delete(`/input/${deleteFileId}`);
      setInputFiles((prev) => prev.filter((f) => f.id !== deleteFileId));
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  const handleDownloadInput = async (record) => {
    try {
      const response = await apiClient.getRaw(
        `/input-path/${encodeURIComponent(record.user_id || userId)}/input/${encodeURIComponent(record.id)}`,
        { responseType: "blob" }
      );
      const link = document.createElement("a");
      link.href = URL.createObjectURL(response);
      const filename =
        Array.isArray(record.files) && record.files.length > 0
          ? record.files[0]
          : record.id;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading input file:", error);
    }
  };

  // Returns a Promise that resolves when the upload (file read + backend
  // POST + auto-create + raw ingest) completes, rejects on error. The
  // dialog awaits this so it can keep its spinner visible and not close
  // before the work is actually done. `onPhase` is an optional progress
  // callback the dialog uses to swap its text from "Reading file…" to
  // "Uploading and processing…".
  const handleFileUpload = (file, onPhase, projectTag) => {
    const epoch_time = new Date().getTime();
    const metadata = {
      id: `${ownerId}-${epoch_time}`,
      filename: file.name,
      user_id: ownerId,
      upload_date: epoch_time,
    };
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64File = reader.result.split(",")[1];
        if (onPhase) onPhase("uploading");
        apiClient
          .post("/upload-file-content", {
            ...metadata,
            data: base64File,
            ...projectPayload(projectTags, projectTag),
          })
          .then((response) => {
            if (projectTag) projectTags.reload();
            setUploadMessage({
              type: response?.type || "info",
              message: response?.message || "File uploaded.",
            });
            fetchData();
            resolve(response);
          })
          .catch((error) => {
            const rawData = error?.response?.data;
            let message = error?.message || "Upload failed.";
            const found = problemsFromError(error);
            let uploadProblems = null;
            if (found) {
              const n = found.findings.length;
              message = `${file.name} was uploaded, but it has ${n} problem${n === 1 ? "" : "s"} that must be fixed before it can be processed.`;
              uploadProblems = { fileName: file.name, message: null, findings: found.findings };
            } else if (rawData && typeof rawData === "object") {
              message = "File uploaded with validation errors.";
            } else if (typeof rawData === "string") {
              message = rawData;
            }
            setUploadMessage({ type: "error", title: found ? "Fix needed" : undefined, message, problems: uploadProblems });
            fetchData();
            reject(new Error(message));
          });
      };
      reader.onerror = () => {
        const message = "Could not read the file.";
        setUploadMessage({ type: "error", message });
        reject(new Error(message));
      };
      if (onPhase) onPhase("reading");
      reader.readAsDataURL(file);
    });
  };

  const selectedRecord = inputFiles.find((f) => f.id === selectedFile);
  const selectedFileName = fileNameOf(selectedRecord);
  // Critical problems block processing (the backend refuses the run too).
  const selectedProblems = blockingFindings(selectedRecord?.findings);
  // In a project's list, runs start only from the caller's own files.
  const selectedNotMine = !!project && !!selectedRecord && selectedRecord.user_id !== user?.id;
  const selectedBlocked = selectedProblems.length > 0 || selectedNotMine;
  const canMove = projectTags.staff || (!project && projectTags.available);

  return (
    <>
      {/* Upload status banner */}
      {uploadMessage && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 1.5,
            backgroundColor:
              uploadMessage.type === "error" ? "#FEF2F2" : "#F0FDF4",
            border: `1px solid ${uploadMessage.type === "error" ? "#FCA5A5" : "#86EFAC"}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: uploadMessage.type === "error" ? "#B91C1C" : "#166534",
              fontWeight: 600,
            }}
          >
            {uploadMessage.title || (uploadMessage.type === "error" ? "Upload error" : "Upload successful")}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#374151", mt: 0.25 }}>
            {uploadMessage.message}
          </Typography>
          {uploadMessage.problems && (
            <Button
              size="small"
              color="error"
              onClick={() => setProblems(uploadMessage.problems)}
              sx={{ mt: 0.5, p: 0, minWidth: 0, textTransform: "none", fontSize: "0.75rem" }}
            >
              View problems
            </Button>
          )}
        </Box>
      )}

      {/* Toolbar row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          pb: 2,
        }}
      >
        {/* Template SOP link */}
        <Tooltip title="Open ContinuousMFA v3.2 Input Template SOP">
          <Box
            component="a"
            href={templateLink}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.6,
              borderRadius: 1.5,
              border: "1px solid #E2E8F0",
              backgroundColor: "#F8FAFC",
              textDecoration: "none",
              color: "#475569",
              fontSize: "0.75rem",
              fontWeight: 500,
              transition: "all 0.13s ease",
              "&:hover": {
                borderColor: "#2F3F5C",
                color: "#2F3F5C",
                backgroundColor: "#EFF6FF",
              },
            }}
          >
            <PdfIcon sx={{ fontSize: 15, color: "#EF4444" }} />
            Input Template SOP
            <OpenInNewIcon sx={{ fontSize: 11, color: "#94A3B8" }} />
          </Box>
        </Tooltip>

        {/* Quick User's Guide link — same styling as the SOP link above,
            opens the same dialog QuickUserGuideAutoShow pops on first login
            (see LicenseGuard.jsx) rather than a raw image in a new tab, so
            there's one consistent viewing experience for it app-wide. */}
        <Tooltip title="Open the Quick User's Guide">
          <Box
            component="button"
            type="button"
            onClick={() => setIsUserGuideOpen(true)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.6,
              borderRadius: 1.5,
              border: "1px solid #E2E8F0",
              backgroundColor: "#F8FAFC",
              color: "#475569",
              fontSize: "0.75rem",
              fontWeight: 500,
              fontFamily: "inherit",
              cursor: "pointer",
              transition: "all 0.13s ease",
              "&:hover": {
                borderColor: "#2F3F5C",
                color: "#2F3F5C",
                backgroundColor: "#EFF6FF",
              },
            }}
          >
            <GuideIcon sx={{ fontSize: 15, color: "#3B82F6" }} />
            Quick User's Guide
          </Box>
        </Tooltip>

        <Box sx={{ flex: 1 }} />

        {/* Input files toggle */}
        <Tooltip title={isInputTableVisible ? "Hide input files" : "Show input files"}>
          <Box
            onClick={() => setIsInputTableVisible((v) => !v)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.25,
              py: 0.6,
              borderRadius: 1.5,
              border: "1px solid #E2E8F0",
              backgroundColor: isInputTableVisible ? "#EFF6FF" : "#F8FAFC",
              cursor: "pointer",
              color: isInputTableVisible ? "#2F3F5C" : "#64748B",
              fontSize: "0.75rem",
              fontWeight: 500,
              userSelect: "none",
              transition: "all 0.13s ease",
              "&:hover": { borderColor: "#2F3F5C", color: "#2F3F5C" },
            }}
          >
            {isInputTableVisible ? (
              <ExpandLessIcon sx={{ fontSize: 16 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16 }} />
            )}
            Input Files
            {inputFiles.length > 0 && (
              <Box
                sx={{
                  ml: 0.5,
                  px: 0.75,
                  py: 0.1,
                  borderRadius: 99,
                  backgroundColor: "#2F3F5C",
                  color: "#fff",
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  lineHeight: 1.6,
                }}
              >
                {inputFiles.length}
              </Box>
            )}
          </Box>
        </Tooltip>

        {/* Upload button */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setUploadDialogOpen(true);
            setIsInputTableVisible(true);
          }}
          sx={{
            borderColor: "#2F3F5C",
            color: "#2F3F5C",
            fontSize: "0.78rem",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: 1.5,
            "&:hover": { backgroundColor: "#EFF6FF", borderColor: "#2F3F5C" },
          }}
        >
          Upload Input
        </Button>

        {/* Run analysis button */}
        <Tooltip
          title={
            !selectedFile
              ? "Select an input file first"
              : selectedNotMine
                ? "Runs start from your own files; this one is another user's"
              : selectedBlocked
                ? "This file has problems that must be fixed before it can be processed"
                : `Run analysis on ${selectedFileName}`
          }
        >
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={<RunIcon />}
              disabled={!selectedFile || selectedBlocked}
              onClick={handleOpenProcessDialog}
              sx={{
                backgroundColor: "#2F3F5C",
                fontSize: "0.78rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 1.5,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#25344b", boxShadow: "none" },
                "&.Mui-disabled": { backgroundColor: "#E2E8F0", color: "#94A3B8" },
              }}
            >
              Run Analysis
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Selected file indicator */}
      {selectedFile && selectedFileName && (
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: "#64748B", fontSize: "0.72rem" }}>
            Selected:
          </Typography>
          <Chip
            label={selectedFileName}
            size="small"
            onDelete={() => { setSelectedFile(null); setAvailableClones([]); setSelectedClones(null); }}
            sx={{
              fontFamily: "monospace",
              fontSize: "0.72rem",
              backgroundColor: "#EFF6FF",
              color: "#2F3F5C",
              border: "1px solid #BFDBFE",
              height: 22,
            }}
          />
        </Box>
      )}

      {selectedBlocked && (
        <Box
          data-testid="blocked-notice"
          sx={{
            mb: 1.5, px: 1.5, py: 1, borderRadius: 1.5,
            backgroundColor: "#FEF2F2", border: "1px solid #FCA5A5",
            display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
          }}
        >
          <ErrorIcon sx={{ fontSize: 16, color: "#EF4444" }} />
          <Typography variant="caption" sx={{ color: "#991B1B", fontWeight: 600 }}>
            This file has {selectedProblems.length} problem{selectedProblems.length === 1 ? "" : "s"} that must be
            fixed before it can be processed.
          </Typography>
          <Button
            size="small"
            color="error"
            onClick={() => showProblems(selectedRecord, selectedProblems)}
            sx={{ p: 0, minWidth: 0, textTransform: "none", fontSize: "0.75rem" }}
          >
            View problems
          </Button>
        </Box>
      )}

      {/* Collapsible file table */}
      <Collapse in={isInputTableVisible}>
        <Box
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: 1.5,
            overflow: "hidden",
            mb: 2,
          }}
        >
          {/* Table header — column widths must match the data-row grid below.
              Radio | Avatar | File info (name + who/when, flex) | Validation
              (Admin only) | Actions. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isAdmin
                ? "36px 40px 1fr 240px 76px"
                : "36px 40px 1fr 170px 76px",
              gap: 0,
              px: 1.5,
              py: 1,
              backgroundColor: "#F8FAFC",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            {["", "", "File", "Validation", ""].map((h, i) => (
              <Typography
                key={i}
                component="div"
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: "#94A3B8",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          {/* File rows */}
          {inputFiles.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                No input files uploaded yet
              </Typography>
            </Box>
          ) : (
            inputFiles.map((record) => {
              const isSelected = record.id === selectedFile;
              const filename = Array.isArray(record.files)
                ? record.files.join(", ")
                : record.files || record.id;
              const displayName = middleTruncate(filename);
              const uploaderLabel = record.user_id
                ? String(record.user_id).split("@")[0]
                : "—";
              const cloneCount = Array.isArray(record.clones) ? record.clones.length : 0;

              // Admin rows show an expanded validation cell to fit the
              // grouped check-name list; users get a compact ready / fix-needed badge.
              const hasFindings = (record.findings?.length || 0) > 0;
              const gridCols = isAdmin
                ? "36px 40px 1fr 240px 76px"
                : "36px 40px 1fr 170px 76px";
              return (
                <Box
                  key={`${record.user_id}/${record.id}`}
                  onClick={() => handleFileSelect(record)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: gridCols,
                    gap: 0,
                    px: 1.5,
                    py: 1,
                    // Top-align so the multi-line badge / two-line info cell
                    // doesn't shove things around unevenly row to row.
                    alignItems: hasFindings ? "flex-start" : "center",
                    cursor: "pointer",
                    backgroundColor: isSelected ? "#EFF6FF" : "#fff",
                    borderBottom: "1px solid #F1F5F9",
                    transition: "background-color 0.1s ease",
                    "&:hover": { backgroundColor: isSelected ? "#EFF6FF" : "#F8FAFC" },
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  {/* Radio */}
                  <Radio
                    checked={isSelected}
                    size="small"
                    sx={{ p: 0, color: "#2F3F5C" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileSelect(record);
                    }}
                  />

                  {/* Uploader avatar — quick visual "who", full email on hover */}
                  <UserAvatar email={record.user_id} size={24} />

                  {/* File info: name on its own line (middle-truncated so both
                      the identifying prefix and the distinguishing suffix stay
                      visible — many uploads share a long common prefix), with
                      uploader / date / clone count demoted to a smaller
                      secondary line instead of competing for the same row. */}
                  <Box sx={{ minWidth: 0, pr: 1 }}>
                    <Tooltip title={filename} placement="top-start">
                      <Typography
                        variant="caption"
                        component="div"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                          color: "#1E293B",
                          fontWeight: isSelected ? 600 : 400,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {displayName}
                      </Typography>
                    </Tooltip>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        fontSize: "0.68rem",
                        color: "#94A3B8",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        mt: 0.1,
                      }}
                    >
                      {uploaderLabel} · {formatDate(record.upload_date)}
                      {cloneCount > 0 ? ` · ${cloneCount} condition${cloneCount === 1 ? "" : "s"}` : ""}
                      {projectTags.staff
                        ? ` · ${record.project_id ? `${record.project_id} · ${record.project_name || ""}` : "not in a project"}`
                        : record.project_tag ? ` · #${record.project_tag}` : ""}
                    </Typography>
                    {canMove && record.record_id && (
                      <Button size="small" sx={{ p: 0, minWidth: 0, fontSize: "0.68rem", textTransform: "none" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMoving({
                                  type: "input", id: record.record_id, label: `${filename} (${record.user_id})`,
                                  current: projectTags.staff ? record.project_id : record.project_tag,
                                });
                              }}>
                        {record.project_id || record.project_tag ? "Move to another project" : "Put in a project"}
                      </Button>
                    )}
                  </Box>

                  {/* Validation — Admins: per-check breakdown, click for the full
                      report. Users: ready, or the problems that block processing. */}
                  <Box>
                    {isAdmin ? (
                      <ValidationBadge
                        record={record}
                        onClickDetail={setValidationReportRecord}
                      />
                    ) : (
                      <UserValidationBadge record={record} onViewProblems={showProblems} />
                    )}
                  </Box>

                  {/* Actions */}
                  <Box
                    sx={{ display: "flex", gap: 0.25 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadInput(record)}
                        sx={{ color: "#64748B", "&:hover": { color: "#2F3F5C" } }}
                      >
                        <DownloadIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    {(!project || record.user_id === user?.id) && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeleteFileId(record.id);
                          setIsDeleteDialogOpen(true);
                        }}
                        sx={{ color: "#64748B", "&:hover": { color: "#EF4444" } }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Collapse>

      <ReportUploadDialog
        isOpen={isUploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={handleFileUpload}
        projectTags={projectTags}
        defaultProject={project && project !== "none" ? project : null}
      />
      <MoveToProjectDialog
        item={moving}
        projectTags={projectTags}
        onClose={() => setMoving(null)}
        onMoved={() => { fetchData(); triggerReportsRefresh(); }}
      />
      <ReportDeleteDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDelete={handleDeleteFile}
      />
      <ReportProcessDialog
        isOpen={isProcessDialogOpen}
        onClose={() => setIsProcessDialogOpen(false)}
        onProcess={(product, usePost, useBio, clones, tag) =>
          handleProcessInputFile(product, usePost, useBio, clones, tag)
        }
        selectedClones={dialogClones}
        setSelectedClones={setDialogClones}
        availableClones={availableClones}
        projectTags={projectTags}
        defaultTag={projectValueOf(projectTags, selectedRecord)}
      />
      <InputValidationReportDialog
        open={!!validationReportRecord}
        onClose={() => setValidationReportRecord(null)}
        record={validationReportRecord}
      />
      <InputProblemsDialog
        open={!!problems}
        onClose={() => setProblems(null)}
        fileName={problems?.fileName}
        message={problems?.message}
        findings={problems?.findings || []}
      />
      <QuickUserGuideDialog
        open={isUserGuideOpen}
        onClose={() => setIsUserGuideOpen(false)}
      />
    </>
  );
}
